import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Accordion, Card, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { capitalize, flatMap, get, set } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import * as multiProductSelectAction from './store/MultiProductSelect.action';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { currencyCodes, convertSelectLabel, inconterms, costinStylesWhiteBordered, checkArray } from '../../../../Components/helper';
import { averageCostingFieldpath, detailedCostingFieldpath, landedInfoFieldPath, commonPath, errorpath } from '../../ProductCosting/fieldPath';
import { ProductInfoTable, AverageCostingTable, QuantitiesTable, DetailedCostingTable, GrandTotal, LandedInformationTable } from './Tables';
import * as Action from '../../ProductCosting/store/ProductCosting.action';
import useValidationHooks from '../../ProductCosting/Hooks/useValidationHooks';
import useShowHideSectionHooks from './Hooks/useShowHideSectionHooks';
import useDetailedPageHook from './Hooks/useDetailedPageHook';
import TestingTable from './Tables/TestingTable';
import AdditionalTable from './Tables/AdditionalTable';
import { NewCheckbox } from '../../../../Components/PIM/common/NewCheckbox';

const ProductCostingDetailView = forwardRef(({ index, globalIndex, devOrderId, findNameOfProduct }, ref) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const initialSetting = {
    productId: null,
    isGlobalUrl: false,
    productInfo: {
      supplierId: null,
      currency: null,
      incoterms: null,
      reference: '',
      shipmentMethod: null,
    },
    averageCosting: {
      averageCostingInfo: [],
      sumOfTotalQuantity: 0,
      totalPricePerUnit: 0,
      averageCostingActive: false,
      overAllSumOfQuantityPerSizCol: 0,
      overAllSumOfTotalPrice: 0,
      overAllSumOfPricePerUnit: 0,
      loader: false,
      totLength: [],
      totSize: [],
      totColourways: [],
    },
    detailedCosting: {
      bomTypeArr: [],
      detailedCostingActive: false,
    },
    testing: {
      testingInfo: [],
      sumOfPrice: '',
    },
    currencyOptionsArr: currencyCodes.map((currKey) => convertSelectLabel(currKey, true)),
    incotermsOptionsArr: inconterms.map((currKey) => convertSelectLabel(currKey, true)),
    colourwayOptionsArr: [],
    sizeOptionsArr: [],
    uomOptionsArr: [],
    status: 'Draft',
    isBreakdown: false,
    quantities: {
      quantitiesInfo: [],
      sumOfTotalQuantity: 0,
      totalPricePerUnit: 0,
      sumOfQuantityPerSizCol: 0,
      overAllSumOfQuantityPerSizCol: 0,
      totLength: [],
      totSize: [],
      totColourways: [],
    },
    lengthOptions: [],
    // new data changes
    productTemplate: null,
    costingTemplate: null,
    landedInfo: {
      landedInfoColumnArr: [],
      landedInfoArr: [],
      currency: null,
      targetCost: '',
    },
    additional: {
      additionalInfo: [],
    },
    isRendered: false,
  };

  const grandTotalObj = {
    grandTotal: [],
    totalBomTypeTotalPrice: '0',
  };

  const initialInternalState = {
    costingComment: '',
    markAsComplete: false,
    isChangedBySupllierState: false,
    activeTab: 'product-costing',
    commentMsg: '',
    productCostCommentModal: false,
    inviteBtn: false,
    hideLengthCol: false,
    primaryCurrValue: 1,
    secondaryCurrValue: 0,
    currSelectedTemplateDetails: null,
    landedExchangeRateInformation: {},
    isDefault: false,
    hideColourCol: false,
  };

  const comStateObj = {
    showHideAlertPopup: false,
    loading: false,
    accodionArray: ['productInfo', 'average', 'detailed', 'quantities', 'bom', 'testing', 'additional', 'grandtotal', 'landed', 'invite'],
  };

  const [productCostingstate, setProductCostingState] = useState(initialSetting);
  const [costingOption, setCostingOption] = useState([]);
  const [grandTotalState, setGrandTotalState] = useState(grandTotalObj);

  const [internalState, setInternalState] = useState(initialInternalState);
  const { landedExchangeRateInformation, productCostCommentModal, commentMsg, isDefault } = internalState;

  const [comState, setState] = useState(comStateObj);
  const {
    UserDetails: { userInfo },
    MultiProductSelectReducer: {
      productColours,
      productSizes,
      productLengths,
      bomCompoList,
      productExchnageRate,
      firstSelection,
      firstSelectionInvite,
      preProductTemplate,
      switchSelection,
    },
    CostingTemplateData: { costingTemplateList },
    SystemMastersDetails: { unitList, selectCostingStageList },
  } = useSelector((reduxState) => reduxState);
  const {
    averageCosting: { averageCostingActive, averageCostingInfo },
    detailedCosting: { detailedCostingActive, bomTypeArr },
    status,
    isBreakdown,
    productInfo: { currency, supplierId, incoterms, reference, shipmentMethod, targetCost },
    productTemplate,
    costingTemplate,
    quantities: { quantitiesInfo },
    testing: { testingInfo },
    additional: { additionalInfo },
    landedInfo: { landedInfoArr },
    colourwayOptionsArr,
    sizeOptionsArr,
  } = productCostingstate;

  const [firstState, setFirstState] = useState({
    switchAdded: false,
  });
  const { switchAdded } = firstState;

  const checkAccodionActiveState = (str) => {
    return comState.accodionArray.includes(str);
  };

  const handleClickToggle = (eventKey) => {
    const copyaccodionArray = comState.accodionArray;
    const accIndex = copyaccodionArray.indexOf(eventKey);
    if (accIndex >= 0) {
      copyaccodionArray.splice(accIndex, 1);
    } else copyaccodionArray.push(eventKey);
    setState((prevState) => ({
      ...prevState,
      accodionArray: copyaccodionArray,
    }));
  };
  const { checkValidation, setGlobalErrors, globalError, onChangeCheckIfErrorExist } = useValidationHooks(productCostingstate);

  useEffect(() => {
    const activeData = selectCostingStageList?.filter((item) => item?.isActive);
    setCostingOption(activeData);
  }, [selectCostingStageList]);

  useEffect(() => {
    const init = () => {
      if (globalIndex > 0) {
        handleTemplateSelect(preProductTemplate);
      }
    };
    if (index === globalIndex && !productCostingstate?.isRendered) {
      init();
    }
  }, [globalIndex]);

  useEffect(() => {
    const init = () => {
      const copyState = productCostingstate;
      if (Array.isArray(productColours) && productColours.length > 0) {
        const ColourwayArr = productColours.map((val) => {
          return { ...val, value: val.name, label: val.name };
        });
        set(copyState, commonPath().COLOURWAY_OPTIONS, ColourwayArr);
      }
      if (Array.isArray(productSizes) && productSizes.length > 0) {
        const sizeArr = productSizes.map((val) => {
          return { value: val, label: val, name: val };
        });
        set(copyState, commonPath().SIZE_OPtIONS, sizeArr);
      }
      if (Array.isArray(unitList) && unitList.length > 0) {
        set(copyState, commonPath().UOM_OPTIONS, unitList);
      }

      let hideLen = false;
      if (checkArray(productLengths)) {
        hideLen = true;
      }
      setInternalState((state) => ({
        ...state,
        hideLengthCol: hideLen,
        ...firstSelectionInvite,
      }));
      setProductCostingState((state) => ({
        ...state,
        ...copyState,
        isRendered: true,
      }));
    };
    if (index === globalIndex) {
      init();
    }
  }, [globalIndex, productColours?.length, productLengths?.length, unitList?.length, productSizes?.length]);

  const productInfoObj = {
    productCostingstate,
    setProductCostingState,
    checkValidation,
    setGlobalErrors,
    globalError,
    initialSetting,
    internalState,
    grandTotalState,
    index,
    globalIndex,
    setGrandTotalState,
    setInternalState,
    devOrderId,
  };

  const { isSupplier, onLoadDisable, isDisabled } = useShowHideSectionHooks(productInfoObj);
  const clrSizeLengthError = 'COSTING_TO_DO_LIST.CLR_SIZ_LEN_ERR';
  const { handleAverageCosting, handleDetailedCosting, handleAdditonalModule, handleLandedInfoModule, manipualtedProductCostingSaveData } =
    useDetailedPageHook(productInfoObj);

  const handleTemplateSelect = async (selected) => {
    if (productColours.length === 0 || productSizes.length === 0) {
      toasterMsg(t(clrSizeLengthError), 'danger');
    } else {
      const res = await dispatch(Action.getProductCostingTemplateDetailsByIds(selected._id, devOrderId));
      let copyState = productCostingstate;
      // populate landed info dynamic column data on templete select
      if (res) {
        setInternalState((prev) => ({
          ...prev,
          currSelectedTemplateDetails: res,
        }));
        set(copyState, commonPath().SELECTED_TEMPLATE, selected);
        const respose = await handleAdditonalModule(res, copyState, true);
        const responsedata = await handleLandedInfoModule(res, respose, true);
        copyState = responsedata;
        // temp hide
        if (process.env.REACT_APP_NODE_ENV === 'development') {
          const responsedata = await handleLandedInfoModule(res, respose, true);
          copyState = responsedata;
        } else {
          // set currency from template data selected
          copyState = respose;
        }
        set(copyState, landedInfoFieldPath().LANDED_INFO_SELECTED_CURRENCY, res.currencyCode);
        set(copyState, landedInfoFieldPath().LANDEDINFO_CURRENCY_ARR, res?.findExcahngeRateCurrency);
        set(copyState, landedInfoFieldPath().LANDED_INFO_VAT, res.vat);
      }
      if (index === globalIndex && !productCostingstate?.isRendered) {
        setProductCostingState((state) => ({
          ...state,
          ...copyState,
          productInfo: {
            ...state.productInfo,
            supplierId: firstSelection?.supplierId,
            currency: firstSelection?.currency,
            incoterms: firstSelection?.incoterms,
            reference: firstSelection?.reference,
            shipmentMethod: firstSelection?.shipmentMethod,
          },
          isBreakdown: switchSelection?.isBreakdown,
        }));
      } else {
        setProductCostingState((state) => ({ ...state, ...copyState }));
      }
    }
  };

  const handleSelectCostingStage = async (selected) => {
    const copyState = productCostingstate;
    set(copyState, commonPath().SELECTED_COSTING_TEMP, selected);
    setProductCostingState((state) => ({ ...state, ...copyState }));
  };

  useEffect(() => {
    const setData = async () => {
      if (colourwayOptionsArr?.length > 0 && sizeOptionsArr?.length > 0 && !switchAdded && index === globalIndex) {
        await handleFirstSwithces();
        setFirstState((prevState) => ({
          ...prevState,
          switchAdded: true,
        }));
      }
    };
    setData();
  }, [colourwayOptionsArr, sizeOptionsArr, productLengths, globalIndex, supplierId]);

  useEffect(() => {
    const callFn = async () => {
      const { supplierId, currency, incoterms, reference, shipmentMethod } = productCostingstate.productInfo;
      const obj = {
        supplierId,
        currency,
        incoterms,
        reference,
        shipmentMethod,
      };
      await dispatch(multiProductSelectAction.setFirstSelectionData('product', obj));
      const obj1 = {
        productTemplate,
      };
      await dispatch(multiProductSelectAction.setFirstSelectionData('temp', obj1));
      const obj3 = {
        averageCostingActive,
        detailedCostingActive,
        isBreakdown,
      };
      await dispatch(multiProductSelectAction.setFirstSelectionData('switch', obj3));
    };
    if (index === 0) {
      callFn();
    }
  }, [productCostingstate, internalState]);

  useEffect(() => {
    const callFn = async () => {
      const { commentMsg, productCostCommentModal } = internalState;
      const obj = {
        commentMsg,
        productCostCommentModal,
      };
      await dispatch(multiProductSelectAction.setFirstSelectionData('invite', obj));
    };
    if (index === 0) {
      callFn();
    }
  }, [internalState]);

  const handleFieldChange = (value, path, changeDependentPath) => {
    const copyState = productCostingstate;
    // when average costing is toggle btn clicked
    if (value && path.includes('averageCosting')) {
      // show popup when detailed costing active
      if (detailedCostingActive) {
        setState((prevState) => ({ ...prevState, showHideAlertPopup: true }));
      } else {
        // check product is having colour,size,length
        if (
          // productLengths.length === 0 ||
          productColours.length === 0 ||
          productSizes.length === 0
        ) {
          toasterMsg(t(clrSizeLengthError), 'danger');
        } else {
          if (changeDependentPath) {
            set(copyState, changeDependentPath, false);
          }
          set(copyState, path, value);
          handleAverageCosting();
        }
      }
    } else if (value && path.includes('detailedCosting')) {
      if (averageCostingActive) {
        setState((prevState) => ({ ...prevState, showHideAlertPopup: true }));
      } else {
        // check product is having colour,size,length
        if (productColours.length === 0 || productSizes.length === 0) {
          toasterMsg(t(clrSizeLengthError), 'danger');
        } else if (bomCompoList.length === 0) {
          toasterMsg(t('COSTING_TO_DO_LIST.ADD_BOM_IN_PRODUCT'), 'danger');
        } else {
          if (changeDependentPath) {
            set(copyState, changeDependentPath, false);
          }
          set(copyState, path, value);
          handleDetailedCosting();
        }
      }
    } else {
      set(copyState, 'detailedCosting', initialSetting.detailedCosting);
      set(copyState, 'averageCosting', initialSetting.averageCosting);
      set(copyState, 'testing', initialSetting.testing);
      set(copyState, 'quantities', initialSetting.quantities);
      set(copyState, 'additional', initialSetting.additional);
    }
    setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
  };

  const handleRadioChange = (value, path) => {
    const copyState = productCostingstate;
    set(copyState, path, value);
    set(copyState, commonPath().ISBREAKDOWNMANUALTOGGLE, true);
    if (checkArray(productColours)) {
      setInternalState((state) => ({
        ...state,
        hideColourCol: false,
      }));
    }
    setProductCostingState((prevState) => ({ ...prevState, ...copyState }));
  };
  const handleFirstSwithces = async () => {
    if (switchSelection.averageCostingActive) {
      handleRadioChange(switchSelection.isBreakdown, commonPath().ISBREAKDOWN);
      handleFieldChange(
        switchSelection.averageCostingActive,
        averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
        detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
      );
    } else {
      handleRadioChange(switchSelection.isBreakdown, commonPath().ISBREAKDOWN);
      handleFieldChange(
        switchSelection.detailedCostingActive,
        detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
        averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
      );
    }
  };

  const getValue = () => {
    if (landedExchangeRateInformation?.manualAdded) {
      return landedExchangeRateInformation?.exchangedRate;
    }
    return internalState?.primaryCurrValue;
  };

  const getDisplayValue = (val) => {
    if (val === 'supplierCurrency') {
      return landedExchangeRateInformation?.supplierCurrency ? landedExchangeRateInformation?.supplierCurrency : currency;
    }
    if (val === 'companyCurrency') {
      return landedExchangeRateInformation?.companyCurrency ? landedExchangeRateInformation?.companyCurrency : userInfo?.companyCurrency;
    }
    const otherVal =
      productExchnageRate?.defaultCurrencyValue && productExchnageRate?.defaultCurrencyValue !== '' ? productExchnageRate?.defaultCurrencyValue : 1;

    return landedExchangeRateInformation?.companyCurrency ? landedExchangeRateInformation?.companyCurrencyValue : otherVal;
  };

  const setLandedCurrencyVal = (e) => {
    setInternalState((prev) => ({
      ...prev,
      landedExchangeRateInformation: {
        ...prev.landedExchangeRateInformation,
        exchangedRate: e,
      },
    }));
  };

  const changeSwitch = (e) => {
    if (!e) {
      setInternalState((prev) => ({
        ...prev,
        landedExchangeRateInformation: {
          ...prev.landedExchangeRateInformation,
          manualAdded: false,
          exchangedRate: internalState?.primaryCurrValue || 1,
        },
      }));
    } else {
      setInternalState((prev) => ({
        ...prev,
        landedExchangeRateInformation: {
          ...prev.landedExchangeRateInformation,
          manualAdded: true,
          exchangedRate: internalState?.primaryCurrValue || 1,
        },
      }));
    }
  };

  const { grandTotal } = grandTotalState;
  const createdSaveDataObj = async () => {
    const flattenedGrandTotal = flatMap(grandTotal?.map((val) => val?.expandData));

    const data = {
      detailedCosting: [],
      averageCosting: [],
      quantities: [],
      testing: [],
      additional: [],
    };
    if (detailedCostingActive) {
      const detailedCostingArr = [];
      if (checkArray(bomTypeArr)) {
        for (const element of bomTypeArr) {
          const obj = {
            bomComponentId: element._id,
            quantityUnitOfMeasure: element?.quantityUnitOfMeasure?._id,
            detailedCostingInfo: await manipualtedProductCostingSaveData(element.detailedCostingInfo, 'detailedCosting'),
          };
          detailedCostingArr.push(obj);
        }
      }
      data.detailedCosting = detailedCostingArr;
      data.quantities = await manipualtedProductCostingSaveData(quantitiesInfo, 'quantities');
      data.testing = await manipualtedProductCostingSaveData(testingInfo, 'testing');
      data.additional = await manipualtedProductCostingSaveData(additionalInfo, 'additional');
    } else if (averageCostingActive) {
      data.averageCosting = await manipualtedProductCostingSaveData(averageCostingInfo, 'averageCosting');
    }
    const landedExchangeRateInformationObj = {
      exchangedRate: internalState?.primaryCurrValue || 1,
      exchangedRateManualFalse: internalState?.primaryCurrValue || 0,
      companyCurrency: userInfo?.companyCurrency,
      supplierCurrency: currency,
      companyCurrencyValue: productExchnageRate?.defaultCurrencyValue ? productExchnageRate?.defaultCurrencyValue : 1,
      manualAdded: internalState?.landedExchangeRateInformation?.manualAdded ? internalState?.landedExchangeRateInformation?.manualAdded : false,
    };
    if (!landedExchangeRateInformationObj.manualAdded) {
      landedExchangeRateInformationObj.exchangedRate = internalState?.primaryCurrValue;
      landedExchangeRateInformationObj.exchangedRateManualFalse = internalState?.primaryCurrValue;
    }
    if (landedExchangeRateInformationObj.manualAdded) {
      landedExchangeRateInformationObj.exchangedRate = internalState?.landedExchangeRateInformation?.exchangedRate;
    }
    data.isInvite = false;
    if (productCostCommentModal) {
      data.comment = commentMsg;
      data.isInvite = true;
    }
    data.colourLibraries = colourwayOptionsArr?.map((col) => col?._id) || [];
    data.legLengths = productLengths?.map((col) => col?._id) || [];
    data.sizeRanges = sizeOptionsArr?.map((col) => col?.value) || [];
    data.isDefault = isDefault;
    data.devorderName = findNameOfProduct(devOrderId);

    return {
      ...data,
      markAsComplete: internalState?.markAsComplete,
      devOrderId,
      supplierId: supplierId?._id,
      currency,
      incoterms,
      reference,
      targetCost,
      shipmentMethod,
      isAverageCosting: averageCostingActive,
      isDetailedCosting: detailedCostingActive,
      landedInformation: await manipualtedProductCostingSaveData(landedInfoArr, 'landedInfo'),
      status,
      grandTotal: await manipualtedProductCostingSaveData(flattenedGrandTotal, 'grandTotal'),
      productTemplate: productTemplate?._id,
      costingStage: costingTemplate?._id,
      isBreakdown,
      landedExchangeRateInformation: landedExchangeRateInformationObj,
    };
  };

  const saveChanges = async (_e) => {
    if (internalState?.landedExchangeRateInformation?.manualAdded === true) {
      if (!internalState?.landedExchangeRateInformation?.exchangedRate) {
        toasterMsg(t('COASTING_TO_DO_LIST.PLEASE_ADD_EXCHANGE)'), 'danger');
        return false;
      }
    }
    if (!checkValidation()) {
      return await createdSaveDataObj();
    }
    return { save: false, devOrderId };
  };

  useImperativeHandle(ref, () => ({
    async submitFun() {
      return await saveChanges();
    },
    checkValidation1() {
      return checkValidation();
    },
  }));

  const updateError = (path) => {
    onChangeCheckIfErrorExist(path, globalError);
  };

  useEffect(() => {
    if (get(globalError, 'productTemplate', '')) {
      updateError(errorpath().SELECT_TEMPLATE);
    }
  }, [productTemplate]);

  const inviteRelatedChange = () => {
    setInternalState((prev) => ({
      ...prev,
      productCostCommentModal: !prev.productCostCommentModal,
    }));
  };

  return (
    <div>
      {comState.loading ? (
        <Loader />
      ) : (
        <div className="position-relative">
          <div className="default-template-section">
            <div className="common-checkbox">
              <NewCheckbox
                size="sm"
                name={`check_${index}`}
                id={`check_${index}`}
                checked={isDefault}
                onChange={() =>
                  setInternalState((prev) => ({
                    ...prev,
                    isDefault: !prev.isDefault,
                  }))
                }
                disabled={isSupplier || onLoadDisable || isDisabled}
                label={<span className="text-medium">{t('FILTER.DEFAULT')}</span>}
              />
            </div>
            <div className="w-340px ml-3">
              <Select
                options={costingTemplateList}
                getOptionValue={(option) => option.name}
                getOptionLabel={(option) => capitalize(option.name)}
                onChange={(selected) => {
                  handleTemplateSelect(selected);
                  if (get(globalError, 'productTemplate', '')) {
                    updateError(errorpath().SELECT_TEMPLATE);
                  }
                }}
                value={productCostingstate?.productTemplate}
                styles={costinStylesWhiteBordered}
                placeholder={t('FILTER.SELECT_COSTING_TEMPLATES')}
                isDisabled={isSupplier || onLoadDisable || isDisabled}
                menuPosition="fixed"
                menuShouldBlockScroll
                menuShouldScrollIntoView
                className="costing-select"
                classNamePrefix="costing-select"
              />
              <span className="error-message position-absolute w-auto">{get(globalError, 'productTemplate', '')}</span>
            </div>
            <div className="w-340px w-xs-100 mt-2 mt-md-0 mr-0 mr-md-1 order-xs-last">
              <Select
                options={costingOption}
                getOptionValue={(option) => option.name}
                getOptionLabel={(option) => capitalize(option.name)}
                onChange={(selected) => {
                  handleSelectCostingStage(selected);
                  if (get(globalError, 'costingTemplate', '')) {
                    updateError(errorpath().SELECT_COSTING);
                  }
                }}
                onFocus={(e) => e.stopPropagation()}
                value={costingTemplate}
                styles={costinStylesWhiteBordered}
                placeholder={t('COSTING_TO_DO_LIST.SELECT_COSTING_STAGE')}
                menuPosition="fixed"
                menuShouldBlockScroll
                menuShouldScrollIntoView
                className="costing-select ml-2"
                classNamePrefix="costing-select"
              />
              <span className="error-message position-absolute w-auto">{get(globalError, 'costingTemplate', '')}</span>
            </div>
          </div>

          <div className="collapse-card product-info-accordion">
            <Accordion activeKey={checkAccodionActiveState('productInfo')}>
              <Card className="common-class-bottom overflow-visible">
                <Card.Header>
                  <span className="f-16 text-bold">{t('FILTER.PRODUCT_INFO')}</span>
                  <Accordion.Toggle
                    className="btn-toggle"
                    as={Button}
                    variant="link"
                    eventKey={checkAccodionActiveState('productInfo')}
                    onClick={() => handleClickToggle('productInfo')}
                  >
                    <i
                      className={
                        checkAccodionActiveState('productInfo') ? 'icon-new-down-arrow-icon text-grey f-24 inverse' : 'f-24 text-grey icon-new-down-arrow-icon'
                      }
                    />
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey={checkAccodionActiveState('productInfo')}>
                  <Card.Body className="py-0">
                    <ProductInfoTable productInfoObj={productInfoObj} />
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </div>
          <div className="collapse-card product-info-accordion">
            <Accordion activeKey={checkAccodionActiveState('average')}>
              <Card className="common-class-bottom">
                <Card.Header>
                  {!isSupplier ? (
                    <div className="d-flex ml-3 align-items-center">
                      <div className="switch-common d-flex  align-items-center design-switch">
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={averageCostingActive}
                          id={`${index}checkbox_1`}
                          onChange={(e) =>
                            handleFieldChange(
                              e.target.checked,
                              averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
                              detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
                            )
                          }
                          disabled={isSupplier || onLoadDisable || isDisabled}
                        />
                        <label className="text-nowrap mb-0 ml-3" htmlFor={`${index}checkbox_1`}>
                          <span className="f-16  text-bold">{t('FILTER.AVERAGE_COSTING')}</span>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="f-16 mb-0 text-bold">{t('FILTER.AVERAGE_COSTING')}</label>
                  )}
                  <Accordion.Toggle
                    className="btn-toggle"
                    as={Button}
                    variant="link"
                    eventKey={checkAccodionActiveState('average')}
                    onClick={() => handleClickToggle('average')}
                  >
                    <i className={checkAccodionActiveState('average') ? 'icon-new-down-arrow-icon f-24 inverse' : 'f-24 icon-new-down-arrow-icon'} />
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey={checkAccodionActiveState('average')}>
                  <Card.Body className="average-body">
                    <span className="d-flex xs-flex-wrap-center">{t('FILTER.SIZE_BREAKDOWN_QUANTITY')}</span>
                    <div className="d-flex mt-3 align-items-center">
                      <p className="custom-radio mb-0 mr-3">
                        <input
                          type="radio"
                          id={`${index}average_yes${index}`}
                          // name="average_yes"
                          value={isBreakdown}
                          checked={!isBreakdown}
                          disabled={isSupplier || onLoadDisable || isDisabled || !averageCostingActive}
                          onChange={(e) => {
                            handleRadioChange(false, commonPath().ISBREAKDOWN);
                            handleFieldChange(
                              e.target.checked,
                              averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
                              detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
                            );
                          }}
                        />
                        <label className="m-0" htmlFor={`${index}average_yes${index}`}>
                          {t('COMMON_LABEL.YES')}
                        </label>
                      </p>
                      <p className="custom-radio mb-0 mr-3">
                        <input
                          type="radio"
                          id={`${index}average_no`}
                          // name="average_no"
                          value={isBreakdown}
                          checked={isBreakdown === true}
                          disabled={isSupplier || onLoadDisable || isDisabled || !averageCostingActive}
                          onChange={(e) => {
                            handleRadioChange(true, commonPath().ISBREAKDOWN);
                            handleFieldChange(
                              e.target.checked,
                              averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
                              detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
                            );
                          }}
                        />
                        <label className="m-0" htmlFor={`${index}average_no`}>
                          {t('COMMON_LABEL.NO')}
                        </label>
                      </p>
                    </div>

                    {[true, false, null, undefined, ''].includes(isBreakdown) && <AverageCostingTable productInfoObj={productInfoObj} />}
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </div>
          {isSupplier && !detailedCostingActive ? null : (
            <div className="collapse-card product-info-accordion">
              <Accordion activeKey={checkAccodionActiveState('detailed')}>
                <Card className="overflow-visible">
                  <Card.Header>
                    {!isSupplier ? (
                      <div className="d-flex ml-3 align-items-center">
                        <div className="switch-common align-items-center d-flex design-switch">
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            id={`${index}checkbox_1_detail`}
                            onChange={(e) =>
                              handleFieldChange(
                                e.target.checked,
                                detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
                                averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
                              )
                            }
                            checked={detailedCostingActive}
                            disabled={isSupplier || onLoadDisable || isDisabled}
                          />
                          <label className="text-nowrap mb-0 ml-3" htmlFor={`${index}checkbox_1_detail`}>
                            <span className="f-16 text-bold">{t('FILTER.DETAILED_COSTING')}</span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="f-16 mb-0 text-bold">{t('FILTER.DETAILED_COSTING')}</label>
                    )}
                    <Accordion.Toggle
                      className="btn-toggle"
                      as={Button}
                      variant="link"
                      eventKey={checkAccodionActiveState('detailed')}
                      onClick={() => handleClickToggle('detailed')}
                    >
                      <i className={checkAccodionActiveState('detailed') ? 'icon-new-down-arrow-icon inverse f-24' : 'icon-new-down-arrow-icon f-24'} />
                    </Accordion.Toggle>
                  </Card.Header>
                  <Accordion.Collapse className="quantities-collapse" eventKey={checkAccodionActiveState('detailed')}>
                    <Card.Body className="costing-body">
                      <span className="d-flex xs-flex-wrap-center">{t('FILTER.SIZE_BREAKDOWN_QUANTITY')}</span>
                      <div className="d-flex mt-3 align-items-center">
                        <p className="custom-radio mb-0 mr-3">
                          <input
                            type="radio"
                            // id="detailed_yes"
                            id={`${index}detailed_yes`}
                            name={`${index}detailed_yes`}
                            value={!isBreakdown}
                            checked={!isBreakdown}
                            disabled={isSupplier || onLoadDisable || isDisabled || !detailedCostingActive}
                            onChange={(e) => {
                              handleRadioChange(false, commonPath().ISBREAKDOWN);
                              handleFieldChange(
                                e.target.checked,
                                detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
                                averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
                              );
                            }}
                          />
                          <label className="m-0" htmlFor={`${index}detailed_yes`}>
                            {t('COMMON_LABEL.YES')}
                          </label>
                        </p>
                        <p className="custom-radio mb-0 mr-3">
                          <input
                            type="radio"
                            id={`${index}detailed_no`}
                            name={`${index}detailed_no`}
                            value={isBreakdown}
                            checked={isBreakdown === true}
                            disabled={isSupplier || onLoadDisable || isDisabled || !detailedCostingActive}
                            onChange={(e) => {
                              handleRadioChange(true, commonPath().ISBREAKDOWN);
                              handleFieldChange(
                                e.target.checked,
                                detailedCostingFieldpath().DETAILED_COSTING_ACTIVE,
                                averageCostingFieldpath().AVERAGE_COSTING_ACTIVE,
                              );
                            }}
                          />
                          <label className="m-0" htmlFor={`${index}detailed_no`}>
                            {t('COMMON_LABEL.NO')}
                          </label>
                        </p>
                      </div>

                      {detailedCostingActive && [true, false, null, undefined, ''].includes(isBreakdown) && (
                        <div className="collapse-card average-colourway-table">
                          <QuantitiesTable
                            checkAccodionActiveState={checkAccodionActiveState}
                            handleClickToggle={handleClickToggle}
                            productInfoObj={productInfoObj}
                          />

                          <DetailedCostingTable
                            checkAccodionActiveState={checkAccodionActiveState}
                            handleClickToggle={handleClickToggle}
                            productInfoObj={productInfoObj}
                          />
                          <Accordion className="common-class-bottom" activeKey={checkAccodionActiveState('testing')}>
                            <Card className="">
                              <Card.Header>
                                <span className="f-16 text-medium">{t('subMenu.menu7')}</span>
                                <Accordion.Toggle
                                  className="btn-toggle"
                                  as={Button}
                                  variant="link"
                                  eventKey={checkAccodionActiveState('testing')}
                                  onClick={() => handleClickToggle('testing')}
                                >
                                  <i
                                    className={
                                      checkAccodionActiveState('testing')
                                        ? 'icon-new-down-arrow-icon inverse text-grey f-24'
                                        : 'icon-new-down-arrow-icon  text-grey f-24'
                                    }
                                  />
                                </Accordion.Toggle>
                              </Card.Header>
                              <Accordion.Collapse eventKey={checkAccodionActiveState('testing')}>
                                <Card.Body className="px-2 py-0">
                                  <TestingTable productInfoObj={productInfoObj} />
                                </Card.Body>
                              </Accordion.Collapse>
                            </Card>
                          </Accordion>
                          <Accordion activeKey={checkAccodionActiveState('additional')}>
                            <Card className="mb-3 overflow-visible">
                              <Card.Header>
                                <span className="f-16 text-medium">{t('FILTER.ADDITIONAL')}</span>
                                <Accordion.Toggle
                                  className="btn-toggle"
                                  as={Button}
                                  variant="link"
                                  eventKey={checkAccodionActiveState('additional')}
                                  onClick={() => handleClickToggle('additional')}
                                >
                                  <i
                                    className={
                                      checkAccodionActiveState('additional')
                                        ? 'icon-new-down-arrow-icon f-24 text-grey inverse'
                                        : 'icon-new-down-arrow-icon text-grey f-24'
                                    }
                                  />
                                </Accordion.Toggle>
                              </Card.Header>

                              <Accordion.Collapse eventKey={checkAccodionActiveState('additional')}>
                                <Card.Body className="py-0">
                                  <AdditionalTable productInfoObj={productInfoObj} />
                                </Card.Body>
                              </Accordion.Collapse>
                            </Card>
                          </Accordion>
                        </div>
                      )}
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
            </div>
          )}
          <div className="collapse-card product-info-accordion average-colourway-table">
            <Accordion className="common-class-bottom" activeKey={checkAccodionActiveState('grandtotal')}>
              <Card>
                <Card.Header>
                  <span className="f-16 text-bold">{t('PROGRESS_INDICATOR.GRAND_TOTAL')}</span>
                  <Accordion.Toggle
                    className="btn-toggle"
                    as={Button}
                    variant="link"
                    eventKey={checkAccodionActiveState('grandtotal')}
                    onClick={() => handleClickToggle('grandtotal')}
                  >
                    <i className={checkAccodionActiveState('grandtotal') ? 'icon-new-down-arrow-icon f-24 inverse' : 'icon-new-down-arrow-icon f-24'} />
                  </Accordion.Toggle>
                </Card.Header>

                <Accordion.Collapse eventKey={checkAccodionActiveState('grandtotal')}>
                  <Card.Body className="py-0">
                    <GrandTotal productInfoObj={productInfoObj} />
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </div>
          {!isSupplier && (
            <div className="collapse-card product-info-accordion average-colourway-table">
              <Accordion activeKey={checkAccodionActiveState('landed')}>
                <Card className="landed-info overflow-visible">
                  <Card.Header>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="f-16 text-bold">{t('FILTER.LENDED_INFORMATION')}</span>
                      <div className="d-flex align-items-center small ml-md-3">
                        <div className="custom-control design-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`${index}checkbox_3`}
                            onChange={(e) => changeSwitch(e.target.checked)}
                            checked={landedExchangeRateInformation?.manualAdded}
                            disabled={isSupplier || onLoadDisable || isDisabled}
                          />
                          <label className="text-nowrap" htmlFor={`${index}checkbox_3`} title="Manual Exchange Rate" />
                        </div>
                        <span className="mr-3 f-16">
                          {t('FILTER.EXCHANGE_RATE')} {getDisplayValue('defaultCurrencyValue')} {getDisplayValue('companyCurrency')} =
                        </span>
                        <input
                          disabled={!landedExchangeRateInformation?.manualAdded || isDisabled}
                          key="i"
                          name="royaltyFee"
                          value={getValue()}
                          type="number"
                          onChange={(e) => setLandedCurrencyVal(e.target.value)}
                          className="form-control landed-input text-left inWid9"
                        />
                        <span className="ml-1 mr-3">{getDisplayValue('supplierCurrency')}</span>

                        {landedExchangeRateInformation?.manualAdded && landedExchangeRateInformation?.updatedBy && (
                          <span>
                            ({t('FILTER.MANUAL_RATE')}{' '}
                            <span>
                              {landedExchangeRateInformation?.updatedBy} {t('FILTER.ON')}
                              <span className="ml-1">
                                {moment(landedExchangeRateInformation?.updatedAt).format('DD-MMM-YYYY')} at{' '}
                                {moment(landedExchangeRateInformation?.updatedAt).format('HH:mm')}
                              </span>
                            </span>
                            )
                          </span>
                        )}
                      </div>
                      <div className="d-inline-flex visible-data align-items-center ml-auto f-16">
                        {!isSupplier ? (
                          <>
                            <span className="icon-Eye-Open-Red-1 f-20 eye-color mr-16" />
                            {t('FILTER.NOT_VISIBLE_SUPLIER')}
                          </>
                        ) : null}
                      </div>
                      <Accordion.Toggle
                        className="btn-toggle ml-4"
                        as={Button}
                        variant="link"
                        eventKey={checkAccodionActiveState('landed')}
                        onClick={() => handleClickToggle('landed')}
                      >
                        <i
                          className={
                            checkAccodionActiveState('landed') ? ' icon-new-down-arrow-icon text-grey f-24 inverse' : ' text-grey f-24 icon-new-down-arrow-icon'
                          }
                        />
                      </Accordion.Toggle>
                    </div>
                  </Card.Header>

                  <Accordion.Collapse eventKey={checkAccodionActiveState('landed')}>
                    <Card.Body className="py-0">
                      <LandedInformationTable productInfoObj={productInfoObj} />
                    </Card.Body>
                  </Accordion.Collapse>
                </Card>
              </Accordion>
            </div>
          )}

          <div className="collapse-card product-info-accordion average-colourway-table">
            <Accordion activeKey={checkAccodionActiveState('invite')}>
              <Card className="">
                <Card.Header>
                  <div className="d-flex ml-3 align-items-center">
                    <div className="switch-common d-flex align-items-center design-switch">
                      <input
                        type="checkbox"
                        className="form-check-input mt-0"
                        checked={productCostCommentModal}
                        id={`${index}checkbox_5`}
                        onChange={inviteRelatedChange}
                        disabled={isSupplier || onLoadDisable || isDisabled}
                      />
                      <label className="text-nowrap mb-0 ml-3" htmlFor={`${index}checkbox_5`}>
                        <span className="f-16 text-bold"> {t('FILTER.INVITE_SUPLIER')}</span>
                      </label>
                    </div>
                  </div>
                  <Accordion.Toggle
                    className="btn-toggle"
                    as={Button}
                    variant="link"
                    eventKey={checkAccodionActiveState('invite')}
                    onClick={() => handleClickToggle('invite')}
                  >
                    <i className={checkAccodionActiveState('invite') ? 'icon-new-down-arrow-icon f-24 inverse' : 'f-24 icon-new-down-arrow-icon'} />
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey={checkAccodionActiveState('invite')}>
                  <Card.Body>
                    {productCostCommentModal && (
                      <textarea
                        className="form-control text-bordered"
                        onChange={(e) =>
                          setInternalState((prev) => ({
                            ...prev,
                            commentMsg: e.target.value,
                          }))
                        }
                        placeholder="Enter Comment"
                        value={commentMsg}
                      />
                    )}
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          </div>
        </div>
      )}
    </div>
  );
});
ProductCostingDetailView.displayName = ProductCostingDetailView;
export default ProductCostingDetailView;
